"use client";

import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Pagination, A11y, Autoplay } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import "swiper/css";
import "swiper/css/pagination";
import "yet-another-react-lightbox/styles.css";
import type { ParagraphGallerySlider as ParagraphGallerySliderData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import { CmsImage } from "@/components/shared/CmsImage/CmsImage";
import styles from "./ParagraphGallerySlider.module.css";

/**
 * Layout 49 - `PARAGRAPH_GALLERY_SLIDER_LAYOUT`: gallery slider.
 *
 * Struktura:
 *   - Row header: text (eyebrow+H2+lead) z lewej + counter "N/M" i prev/next arrows w prawym gornym rogu
 *   - Swiper 3 slajdow na raz (desktop) z tytulami pod zdjeciem
 *   - Lightbox po kliku slajdu
 *   - Pagination dots pod slajdami (Swiper Pagination module)
 *
 * Caption per slajd: `img.description` z fallback do `img.alt`.
 */
export function ParagraphGallerySlider({
  paragraph,
  t,
}: {
  paragraph: ParagraphGallerySliderData;
  t: ParagraphTranslations;
}) {
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  const {
    text_1: eyebrow,
    name: title,
    text: lead,
    autoplay_delay: autoplayDelay,
    images,
  } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasLead = lead && lead.trim() !== "";
  const hasImages = Array.isArray(images) && images.length > 0;

  //Caption per slajd: img.description (glowne pole) z fallback do img.alt.
  const captionFor = (img: (typeof images)[number]) =>
    (img.description && img.description.trim() !== "" ? img.description : img.alt) ?? "";

  const slides = (images ?? []).map((img) => ({
    src: img.full?.url || img.url,
    width: img.full?.width ?? img.width ?? undefined,
    height: img.full?.height ?? img.height ?? undefined,
    alt: img.alt ?? img.description ?? "",
    description: captionFor(img) || undefined,
  }));

  const total = images?.length ?? 0;
  const showNav = total > 1;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasLead ? (
          <HtmlText html={lead} className={styles.lead} />
        ) : null}
      </div>

      {hasImages && showNav ? (
        <div className={styles.controlsRow}>
          <p className={styles.counter}>
            {activeIdx + 1} / {total}
          </p>
          <div className={styles.controls}>
            <button
              type="button"
              className={`${styles.navBtn} ${styles.navBtnPrev}`}
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label={t.gallery.openImage}
            />
            <button
              type="button"
              className={`${styles.navBtn} ${styles.navBtnNext}`}
              onClick={() => swiperRef.current?.slideNext()}
              aria-label={t.gallery.openImage}
            />
          </div>
        </div>
      ) : null}

      {hasImages ? (
        <div className={styles.sliderWrap}>
          <Swiper
            modules={[Pagination, A11y, Autoplay]}
            slidesPerView={1}
            spaceBetween={16}
            loop={images.length > 3}
            pagination={images.length > 1 ? { clickable: true } : false}
            a11y={{ enabled: true }}
            autoplay={
              autoplayDelay > 0 && images.length > 1
                ? { delay: autoplayDelay * 1000, disableOnInteraction: false, pauseOnMouseEnter: true }
                : false
            }
            breakpoints={{
              640: { slidesPerView: 2, spaceBetween: 20 },
              1024: { slidesPerView: 3, spaceBetween: 24 },
            }}
            onSwiper={(s) => { swiperRef.current = s; }}
            onSlideChange={(s) => setActiveIdx(s.realIndex)}
            className={styles.swiper}
          >
            {images.map((img, i) => {
              const caption = captionFor(img);
              return (
                <SwiperSlide key={img.id} className={styles.slide}>
                  <button
                    type="button"
                    onClick={() => setLightboxAt(i)}
                    aria-label={img.alt ?? caption ?? t.gallery.openImage}
                    className={styles.slideBtn}
                  >
                    <span className={styles.slideImgWrap}>
                      <CmsImage
                        src={img.url}
                        alt={img.alt ?? caption ?? ""}
                        width={img.width ?? 800}
                        height={img.height ?? 600}
                        sizes="(max-width: 40rem) 100vw, (max-width: 64rem) 50vw, 33vw"
                        priority={i === 0}
                        className={styles.slideImg}
                      />
                    </span>
                    {caption ? (
                      <span className={styles.slideCaption}>{caption}</span>
                    ) : null}
                  </button>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      ) : (
        <div className={styles.empty}>{t.gallery.empty}</div>
      )}

      <Lightbox
        open={lightboxAt !== null}
        index={lightboxAt ?? 0}
        close={() => setLightboxAt(null)}
        slides={slides}
      />
    </section>
  );
}
